import { cn } from "@/lib/utils"
import { type LucideIcon } from "lucide-react"

export type StatTileTone = "default" | "success" | "warning" | "danger"

const valueToneClass: Record<StatTileTone, string> = {
  default: "text-white",
  success: "text-success",
  warning: "text-warning",
  danger: "text-red1",
}

export interface StatTileProps {
  label: string
  value: number | string
  icon: LucideIcon
  tone?: StatTileTone
  /** Turns the tile into a toggle button, e.g. a list filter. */
  onClick?: () => void
  /** Whether the toggle is on. Only read together with `onClick`. */
  pressed?: boolean
}

export function StatTile({
  label,
  value,
  icon: Icon,
  tone = "default",
  onClick,
  pressed = false,
}: StatTileProps) {
  // Spans, not divs: the same content has to be valid inside a <button>.
  const content = (
    <span className="flex items-start justify-between gap-2">
      <span>
        <span className="block text-[11px] font-medium uppercase leading-tight tracking-[0.04em] text-grey5">
          {label}
        </span>
        <span
          className={cn(
            "mt-1 block text-[32px] font-semibold leading-tight tracking-tight",
            valueToneClass[tone],
          )}
        >
          {value}
        </span>
      </span>
      <Icon size={16} className="mt-0.5 shrink-0 text-grey5" />
    </span>
  )

  if (!onClick) {
    return (
      <div className="rounded-xl border border-grey4 bg-grey3 p-[18px]">
        {content}
      </div>
    )
  }

  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "w-full rounded-xl border p-[18px] text-left transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
        pressed
          ? "border-yellow1 bg-yellow1/10"
          : "border-grey4 bg-grey3 hover:border-grey5",
      )}
    >
      {content}
    </button>
  )
}
